import { Chip, Divider, List, OverlayTrigger } from '@hai/ui-react';
import { isNil } from 'ramda';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import BusyIcon from 'src/js/component/base/busy-icon';
import { triggerEvent, watchEvent } from 'src/js/events';
import { useMutable } from 'src/js/hook/use-mutable';
import { useStores } from 'src/js/hook/use-stores';
import { scrollPanelIntoView } from 'src/js/component/actionBar/streaming-action-bar';
import { constant } from 'src/js/constant';

import {
  AudioAlgorithmEnum,
  AudioEncoderViewModel,
  encoderIsMuted,
  encoderIsStopped,
  encoderStateToColor,
  encoderStateToIcon,
} from './audio-encoder-models';
import { AudioEncoderEdit } from './audio-encoder-edit';
import { AudioEncoderStatistics } from './audio-encoder-statistics';
import { muteUnmuteAudioEncoder, startStopAudioEncoder } from './audio-encoder-store';
import { generateDataAuto } from 'src/js/util/automation';
import streams from '../stream/stream-store';
import { StreamEncapsulation, StreamState } from '../stream/stream-models';

interface AudioEncoderItemProps {
  model: AudioEncoderViewModel;
  onSelect: any;
  scrollToId: number | string;
}

export const AudioEncoderRow: React.FunctionComponent<AudioEncoderItemProps> = ({ model, onSelect, scrollToId }) => {
  const { t } = useTranslation();
  const { sessionStore } = useStores();

  const location = useLocation();
  const showStats = location?.state?.stats === true;

  const scroll = location?.state?.id === model.id || scrollToId === model.id;

  const [isExpanded, setIsExpanded] = useState<boolean>(scroll);
  const [collapseTimeoutId, setCollapseTimeoutId] = useMutable(null);
  const [expandContent, setExpandContent] = useState<string>(scroll ? (showStats ? 'STATISTICS' : 'SETTINGS') : null);
  const onExpandedStateChange = (expandedState: { eventKey: any; expanded: boolean }) => {
    if (isExpanded !== expandedState.expanded) {
      setIsExpanded(expandedState.expanded);
    }
  };

  watchEvent('expandAll:edit', () => {
    setExpandContent('SETTINGS');
    setIsExpanded(true);
  });
  watchEvent('expandAll:stats', () => {
    setExpandContent('STATISTICS');
    setIsExpanded(true);
  });
  const collapseAll = () => {
    setIsExpanded(false);
    setCollapseTimeoutId(
      setTimeout(() => {
        // the component needs to stay rendered during close for smooth closing
        setExpandContent(null);
        setCollapseTimeoutId(null);
      }, constant.collapseTime),
    );
  };
  watchEvent('collapseAll:edit', collapseAll);
  watchEvent('collapseAll:stats', collapseAll);

  const [isMuting, setIsMuting] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const onSelectHandler = (eventKey: string, expanded: boolean, ev: KeyboardEvent) => {
    switch (eventKey) {
      case 'STATISTICS':
      case 'SETTINGS':
        if (ev && ev.shiftKey) {
          // expend all
          if (expanded) {
            eventKey === 'SETTINGS' ? triggerEvent('expandAll:edit') : triggerEvent('expandAll:stats');
          } else {
            eventKey === 'SETTINGS' ? triggerEvent('collapseAll:edit') : triggerEvent('collapseAll:stats');
          }
          return;
        }
        setExpandContent(eventKey);
        if (expanded) {
          setIsExpanded(true);
          if (collapseTimeoutId() !== null) {
            clearTimeout(collapseTimeoutId());
            setCollapseTimeoutId(null); // prevent closing of panel
          }
          scroll && scrollPanelIntoView({ id: model.id });
        } else {
          setIsExpanded(false);
          setCollapseTimeoutId(
            setTimeout(() => {
              // the component needs to stay rendered during close for smooth closing
              setExpandContent(null);
              setCollapseTimeoutId(null);
            }, constant.collapseTime),
          );
        }
        break;
      case 'MUTE':
        setIsMuting(true);
        muteUnmuteAudioEncoder(model, encoderIsMuted(model.state), false, t).then(() => {
          triggerEvent('preset-update');
          setIsMuting(false);
        });
        break;
      case 'PLAY':
        setIsUpdating(true);
        startStopAudioEncoder(model, encoderIsStopped(model.state), false, t).then(() => {
          triggerEvent('preset-update');
          setIsUpdating(false);
        });
        break;
      default:
        break;
    }
  };
  useEffect(() => {
    return () => {
      // final cleanup
      if (collapseTimeoutId() !== null) {
        clearTimeout(collapseTimeoutId());
      }
    };
  }, []);

  const btnId = `audenc-settings-${model.id}`;
  const handleCancelEdit = () => {
    const btn = document.getElementById(btnId);
    if (!isNil(btn)) {
      btn.click();
    }
  };

  const onClickListPanel = (ev: UIEvent) => {
    ev?.stopPropagation();
    ev?.preventDefault();
    if (
      (ev.target as any)?.className !== undefined /*
      (ev.target as any)?.className?.includes?.('HaiListPanelTitle') ||
      (ev.target as any)?.className?.includes?.('list-panel-main-content')*/
    ) {
      if (expandContent == null) {
        setExpandContent('SETTINGS');
      }
      // so we dont expand empty content
      setIsExpanded(!isExpanded);
    }
  };

  const statusColor = encoderStateToColor(model.state);
  
  const associatedStreams = streams.filter((s) => s.audio && s.audio.length > 0 && s.audio[0].id === model.id);
  //console.log('associatedStreams', associatedStreams);
  const anyStartedWhipStream = associatedStreams.find((s) => s.encapsulation === StreamEncapsulation.WHIP && s.state !== StreamState.STOPPED) !== undefined;
  const stopIsDisabledForWhip = !encoderIsStopped(model.state) && anyStartedWhipStream;
  //console.log('stopIsDisabledForWhip', stopIsDisabledForWhip, anyStartedWhipStream, associatedStreams);

  return (
    <div
      id={`panel-${model.id}`}
      className={model.hidden ? 'mk-row-hidden' : ''}
      data-auto={generateDataAuto('row', model.hidden ? 'hidden' : 'visible')}
      style={{ scrollMarginTop: '21px' }}
    >
      <List.Panel
        onClick={onClickListPanel}
        checked={model.selected}
        onSelect={(selected: boolean) => onSelect(model, selected)}
        panelColor={statusColor}
        expandedState={{ eventKey: expandContent, expanded: isExpanded }}
        onExpandedStateChange={onExpandedStateChange}
      >
        <List.PanelTitle
          onClick={onClickListPanel}
          useFsMask={true}
          title={model.name}
          statusIcon={encoderStateToIcon(model.state)}
          iconColor={statusColor}
        />
        <List.PanelDetail>
          <List.PanelDetailItem
            divider="vertical"
            text={t('general.id')}
            data-auto={generateDataAuto('list_panel', 'detail id')}
          >
            <Chip size="sm">{model.id.toString()}</Chip>
          </List.PanelDetailItem>

          <List.PanelDetailItem
            divider="vertical"
            text={model.interfaceTxt}
            icon="Input"
            data-auto={generateDataAuto('list_panel', 'detail input')}
          />

          <List.PanelDetailItem text={model.modeTxt} data-auto={generateDataAuto('list_panel', 'detail mode')}  divider="vertical" />

          <List.PanelDetailItem text={t(`audioEncoder.algorithm.${model.algorithm}`)} data-auto={generateDataAuto('list_panel', 'detail algorithm')} />

          {model.algorithm !== AudioAlgorithmEnum.RAW && (
            <Divider type="vertical" className="mk-detail-divider-workaround" />
          )}
          {model.algorithm !== AudioAlgorithmEnum.RAW && (
            <List.PanelDetailItem
              text={`${model.bitRate}kbps`}
              data-auto={generateDataAuto('list_panel', 'detail bitrate')}
            />
          )}
          
          {model.langTxt && <Divider type="vertical" className="mk-detail-divider-workaround" />}
          {model.langTxt && (
            <List.PanelDetailItem text={model.langTxt} data-auto={generateDataAuto('list_panel', 'detail language')} />
          )}

        </List.PanelDetail>
        {sessionStore.isUser() ? (
          <List.Actions>
            <List.ActionItem
              expandButton
              eventKey="STATISTICS"
              icon="ReportStats"
              title={t('general.statistics')}
              onSelect={onSelectHandler}
            />
            <List.ActionItem
              expandButton
              eventKey="SETTINGS"
              icon="Settings"
              title={t('general.settings')}
              onSelect={onSelectHandler}
              id={btnId}
            />
          </List.Actions>
        ) : (
          <List.Actions>
            <List.ActionItem
              expandButton
              eventKey="STATISTICS"
              icon="ReportStats"
              title={t('general.statistics')}
              onSelect={onSelectHandler}
            />
            <List.ActionItem
              expandButton
              eventKey="SETTINGS"
              icon="Settings"
              title={t('general.settings')}
              onSelect={onSelectHandler}
              id={btnId}
            />
            <List.ActionItem isDivider />
            <List.ActionItem
              eventKey="MUTE"
              icon={
                <BusyIcon
                  isActive={!encoderIsMuted(model.state)}
                  activeIcon="AudioOn"
                  inactiveIcon="AudioOff"
                  busy={isMuting}
                />
              }
              showActive={false}
              onSelect={onSelectHandler}
              disabled={encoderIsStopped(model.state) || isMuting}
            />
            <List.ActionItem
              eventKey="PLAY"
              icon={
              <OverlayTrigger popoverContent={stopIsDisabledForWhip ? t('videnc.tooltips.stopVideoEncoderWithWhip') : undefined} placement="top" onClick={(e:any) => stopIsDisabledForWhip && e.stopPropagation()} >
                <BusyIcon
                  isActive={!encoderIsStopped(model.state)}
                  activeIcon="StopFilled"
                  inactiveIcon="PlayFilled"
                  busy={isUpdating}
                  disabled={stopIsDisabledForWhip}
                />
               </OverlayTrigger>
              }
              showActive={false}
              onSelect={onSelectHandler}
              disabled={isUpdating}
            />
          </List.Actions>
        )}
        <List.ExpandedPanel>
          {expandContent === 'STATISTICS' && isExpanded && <AudioEncoderStatistics model={model} />}
          {expandContent === 'SETTINGS' && isExpanded && <AudioEncoderEdit model={model} onCancel={handleCancelEdit} />}
        </List.ExpandedPanel>
      </List.Panel>
    </div>
  );
};
